import React, { useEffect, useMemo, useState } from 'react';
import { useAPI, useValidator } from '@arema/components/Hooks';
import { addCommas, fromUnix, fromUnixLocal, getNowLocal } from '@arema/components/Util';
import { Button, Field, Groupper, Header, Input, Message, SegmentControl, Table } from 'react-frontier2';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import API from '../../API';

enum ReportType{
	DETAILED = 0,
	SUMMARY = 1,
}

var EventReport = ()=>{
	var { t } = useTranslation();
	var dates = useValidator({
		date_start: getNowLocal().plus({ days: -7 }).toSeconds(),
		date_end: getNowLocal().toSeconds(),
	}, {
		date_start: ['empty', { rule: (val, form)=>val<=form.date_end, prompt: t('reports.event_list.prompts.date_start') }],
		date_end: ['empty', { rule: (val, form)=>val>=form.date_start, prompt: t('reports.event_list.prompts.date_end') }],
	});
	var { data, load, loading } = useAPI(API.getEventListReport, {
		unexpected_error: t('common.unexpected_code', { code: 'LCL-RPEVRPT-1' }),
		setLoadErrorArray: dates.setPrompts
	});
	var [reportType, setReportType] = useState<ReportType>(ReportType.DETAILED)

	var searchEvents = ()=>{
		var { valid } = dates.validate();
		if(!valid) return;
		load(dates.data.date_start, dates.data.date_end);
	}

	var {
		totals
	} = useMemo(()=>{
		var totals = { events: 0, dates: 0, cities: 0 }
		var cities : { city: string, amount: number }[] = [];

		if(data?.data && data?.data.length>0){
			for(var i of data.data){
				totals.events += 1;
				totals.dates += i.dates.length;
				for(var d of i.dates){
					var cx = cities.findIndex(a=>a.city===d.city);
					if(cx==-1){
						cities.push({ city: d.city, amount: 1 })
					}else{
						cities[cx].amount += 1;
					}
				}
			}
		}
		totals.cities = cities.length;
		return { totals };
	}, [data]);
	
	return <Groupper fitted title={t('reports.event_list.title')} style={{ maxWidth: 1200, margin: 'auto' }} titleRight={(
		<Button text={t('common.search')} color='black' style={{ minWidth: 120 }} iconName='search' onClick={searchEvents} loading={loading} />
	)}>
		<Field amount={2} style={{ padding: 20, paddingBottom: 0 }}>
			<Input label={t('common.date_start')} calendar={{ 
				date: fromUnixLocal(dates.data.date_start).toJSDate(), 
				mode: 'date', 
				format: 'dd/MMM/yy',
				onSelected: v=>dates.onDataChange('date_start')(Math.floor(v.getTime()/1000)),
			}} />
			<Input label={t('common.date_end')} calendar={{ 
				date: fromUnixLocal(dates.data.date_end).toJSDate(), 
				mode: 'date', 
				format: 'dd/MMM/yy',
				onSelected: v=>dates.onDataChange('date_end')(Math.floor(v.getTime()/1000)),
			}} style={{ marginRight: 0 }} />
		</Field>
		{data?.data!==null && data?.data.length>0 && <>
			<SegmentControl
				style={{ margin: 15, marginBottom: 0, width: 'auto' }}
				onClick={setReportType}
				segments={[
					{ text: t('reports.event_list.segment_details'), value: 0 },
					{ text: t('reports.event_list.segment_summary'), value: 1 },
				]}
				selected={[reportType]}
			/>
		</>}
		<Message type='error' list={dates.prompts} style={{ margin: 15 }} />
		{(data===null || data?.data.length===0) ? (
			<Groupper.Divider type='dashed' style={{ marginLeft: 0, marginRight: 0 }} />
		) : (
			<div style={{ height: 15 }} />
		)}
		{data===null ? (
			<Header iconName='masks-theater' style={{ paddingTop: 30, paddingBottom: 30, margin: 0 }} iconStyle={{ fontSize: 50 }} text={t('reports.event_list.search_events')} subtext={t('reports.event_list.search_events_text')} actions={(
				<Button text={t('common.search')} style={{ lineHeight: '16px' }} color='black' onClick={searchEvents} loading={loading} />
			)} />
		) : data?.data.length==0 ? (
			<Header iconName='frown-open' style={{ paddingTop: 30, paddingBottom: 30, margin: 0 }} iconStyle={{ fontSize: 50 }} text={t('reports.event_list.empty_title')} subtext={t('reports.event_list.empty_subtitle')} actions={(
				<Button text={t('common.search')} style={{ lineHeight: '16px' }} color='black' onClick={searchEvents} loading={loading} />
			)} />
		) : <>
			{(!!totals && !!data?.data) && (
				<Table>
					<Table.Body>
						<Table.Row>
							<Table.Cell style={{ fontWeight: 'bold', fontSize: 16, lineHeight: '16px' }}>
								{t('reports.event_list.event_count', { count: totals.events })},{' '}
								{t('reports.event_list.date_count', { count: totals.dates })} <br/>
								<span style={{ fontSize: 14, lineHeight: '14px', color: 'gray', fontWeight: 'normal' }}>{t('reports.event_list.city_count', { count: totals.cities })}</span>
							</Table.Cell>
							<Table.Cell collapsing>
								<Button color='blue' text={t('common.download')} iconName={'file-download'} menu={(
									data.downloads.map(a=>(
										{ text: a.text, iconName: a.icon, as: Link, to: a.url, target: '_blank' }
									))
								)} />
							</Table.Cell>
						</Table.Row>
					</Table.Body>
				</Table>
			)}
			{reportType===ReportType.DETAILED ? (
				data?.data.map((a, ix)=>{
					var totals = {
						tickets_sold: 0,
						commission: 0,
						commission_return: 0,
						ticket_cost: 0,
					};
					for(var i of a.dates){
						totals.tickets_sold += i.tickets_sold;
						totals.commission += i.commission;
						totals.commission_return += a.commission_return ? i.ticket_cost*a.commission_return : 0;
						totals.ticket_cost += i.ticket_cost;
					}
					return <React.Fragment key={`EVT-${a.event_id}`}>
						<Table details striped>
							<Table.Body>
								<Table.Row>
									<Table.Cell style={{ minWidth: 70, padding: 0 }} centered>
										<Link className='normal' to={`/events/${a.event_id}`}>{a.event_id}</Link>
									</Table.Cell>
									<Table.Cell style={{ fontWeight: 'bold' }}>
										<Link className='normal' to={`/events/${a.event_id}`}>{a.event_name}</Link>
									</Table.Cell>
								</Table.Row>
								<Table.Row>
									<Table.Cell>Promotor</Table.Cell>
									<Table.Cell><Link className='normal' to={`/promoter/${a.promoter_id}`}>{a.promoter_name}</Link></Table.Cell>
								</Table.Row>
							</Table.Body>
						</Table>
						<Table striped>
							<Table.Head data={[t('reports.event_list.table_headers', { returnObjects: true })]} />
							<Table.Body>
								{a.dates.map(b=>(
									<Table.Row key={`EVDT-${b.date_id}`} collapsingIndexes={[0,1,3,4,5,6,7,8,9]} data={[
										<Link className='normal' to={`/events/${a.event_id}/dates/${b.date_id}`}>{b.date_id}</Link>, 
										fromUnix(b.date).toFormat('dd/MMM/yy HH:mm'),
										<Link className='normal' to={`/venues/${b.venue_id}`}>{b.venue_name}</Link>,
										b.city, 
										addCommas(b.ticket_count, false),
										addCommas(b.tickets_sold, false), 
										addCommas(b.commission), 
										addCommas(a.commission_return ? b.ticket_cost*a.commission_return : 0),
										addCommas(b.ticket_cost), 
										addCommas(b.commission+b.ticket_cost)
									]} />
								))}
							</Table.Body>
							{a.dates.length>1 && (
								<Table.Footer>
									<Table.Row>
										<Table.Cell style={{ textAlign: 'right', fontWeight: 'bold' }} colSpan={5}>{t('common.total')}</Table.Cell>
										<Table.Cell>{addCommas(totals.tickets_sold, false)}</Table.Cell>
										<Table.Cell>{addCommas(totals.commission)}</Table.Cell>
										<Table.Cell>{addCommas(totals.commission_return)}</Table.Cell>
										<Table.Cell>{addCommas(totals.ticket_cost)}</Table.Cell>
										<Table.Cell>{addCommas(totals.commission+totals.ticket_cost)}</Table.Cell>
									</Table.Row>
								</Table.Footer>
							)}
						</Table>
						{ix<data?.data.length-1 && (
							<div style={{ width: '100%', height: 30, backgroundColor: '#f5f5f5' }} />
						)}
					</React.Fragment>
				})
			) : reportType===ReportType.SUMMARY ? (
				<Table striped>
					<Table.Head data={[t('reports.event_list.summary_table_headers', { returnObjects: true })]} />
					<Table.Body>
						{data?.data.map(a=>(
							<React.Fragment key={`EVDT-${a.event_id}`}>
								{a.dates.map(d=>(
									<Table.Row
										key={`DTRW-${d.date_id}`}
										collapsingIndexes={[0, 2, 3, 5, 6, 7, 8]}
										data={[
											<Link className='normal' to={`/events/${a.event_id}`}>{a.event_id}</Link>,
											a.event_name,
											<Link className='normal' to={`/events/${a.event_id}/dates/${d.date_id}`}>{d.date_id}</Link>,
											fromUnix(d.date).toFormat('dd/MMM/yy HH:mm'),
											<Link className='normal' to={`/venues/${d.venue_id}`}>{d.venue_name}</Link>,
											d.city,
											`${addCommas(d.ticket_count, false)}${d.has_infinite ? '+' : ''}`,
											addCommas(d.tickets_sold, false),
											<>
												<span className="ar money-sign">$</span>
												{addCommas(d.ticket_cost+d.commission)}
											</>,
										]}
									/>
								))}
							</React.Fragment>
						))}
					</Table.Body>
				</Table>
			) : null}
		</>}
	</Groupper>
}

export default EventReport;